import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';

const MapView = ({ businesses, selectedBusiness, onBusinessSelect, userLocation, mapCenter, onMapMove }) => {
  const mapRef = useRef(null);
  const [zoom, setZoom] = useState(14);
  const [clusters, setClusters] = useState([]);

  const mockBusinessLocations = businesses?.map((business, index) => ({
    id: business?.id,
    lat: 43.2985 + (Math.random() - 0.5) * 0.02,
    lng: 13.4531 + (Math.random() - 0.5) * 0.02,
    business
  }));

  useEffect(() => {
    if (zoom >= 15) {
      setClusters(mockBusinessLocations?.map(loc => ({
        ...loc,
        isCluster: false,
        count: 1
      })));
    } else {
      const clusterGroups = {};
      mockBusinessLocations?.forEach(loc => {
        const clusterKey = `${Math.floor(loc?.lat * 100)}_${Math.floor(loc?.lng * 100)}`;
        if (!clusterGroups?.[clusterKey]) {
          clusterGroups[clusterKey] = {
            lat: loc?.lat,
            lng: loc?.lng,
            businesses: [],
            isCluster: true
          };
        }
        clusterGroups?.[clusterKey]?.businesses?.push(loc?.business);
      });

      setClusters(Object.values(clusterGroups)?.map((group, index) => ({
        id: `cluster-${index}`,
        lat: group?.lat,
        lng: group?.lng,
        isCluster: group?.businesses?.length > 1,
        count: group?.businesses?.length,
        businesses: group?.businesses
      })));
    }
  }, [zoom, businesses]);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 1, 18));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 1, 10));
  };

  const handleClusterClick = (cluster) => {
    if (cluster?.isCluster && cluster?.count > 1) {
      setZoom(prev => Math.min(prev + 2, 18));
    } else if (cluster?.business) {
      onBusinessSelect(cluster?.business);
    }
  };

  const handleRecenter = () => {
    if (userLocation) {
      onMapMove({ lat: userLocation?.lat, lng: userLocation?.lng });
    }
  };

  return (
    <div className="relative w-full h-full bg-muted rounded-lg overflow-hidden">
      <iframe
        ref={mapRef}
        width="100%"
        height="100%"
        loading="lazy"
        title="Mappa Interattiva di Macerata"
        referrerPolicy="no-referrer-when-downgrade"
        src={`https://www.google.com/maps?q=${mapCenter?.lat},${mapCenter?.lng}&z=${zoom}&output=embed`}
        className="w-full h-full"
      />
      <div className="absolute top-4 left-4 right-4 flex items-center justify-between pointer-events-none z-10">
        <div className="bg-card rounded-lg shadow-warm px-4 py-2 pointer-events-auto">
          <p className="font-caption text-sm text-foreground">
            <span className="font-semibold">{businesses?.length}</span> attività trovate
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleRecenter}
            className="w-10 h-10 bg-card rounded-lg shadow-warm flex items-center justify-center transition-smooth hover:bg-primary/10 focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3 pointer-events-auto"
            aria-label="Centra sulla mia posizione"
          >
            <Icon name="Locate" size={20} color="var(--color-primary)" />
          </button>
        </div>
      </div>
      <div className="absolute bottom-4 right-4 flex flex-col gap-2 z-10">
        <button
          onClick={handleZoomIn}
          className="w-10 h-10 bg-card rounded-lg shadow-warm flex items-center justify-center transition-smooth hover:bg-primary/10 focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3"
          aria-label="Zoom in"
        >
          <Icon name="Plus" size={20} color="var(--color-foreground)" />
        </button>
        <button
          onClick={handleZoomOut}
          className="w-10 h-10 bg-card rounded-lg shadow-warm flex items-center justify-center transition-smooth hover:bg-primary/10 focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3"
          aria-label="Zoom out"
        >
          <Icon name="Minus" size={20} color="var(--color-foreground)" />
        </button>
      </div>
      <div className="absolute inset-0 pointer-events-none z-5">
        {clusters?.map((cluster) => {
          const offsetX = ((cluster?.lng - mapCenter?.lng) * 10000) + 50;
          const offsetY = ((mapCenter?.lat - cluster?.lat) * 10000) + 50;

          return (
            <button
              key={cluster?.id}
              onClick={() => handleClusterClick(cluster)}
              className="absolute pointer-events-auto transition-smooth hover:scale-110 focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3 rounded-full"
              style={{
                left: `${offsetX}%`,
                top: `${offsetY}%`,
                transform: 'translate(-50%, -50%)'
              }}
              aria-label={cluster?.isCluster ? `Cluster di ${cluster?.count} attività` : cluster?.business?.name}
            >
              {cluster?.isCluster ? (
                <div className="w-12 h-12 bg-primary rounded-full shadow-warm-lg flex items-center justify-center">
                  <span className="font-heading font-bold text-lg text-primary-foreground">
                    {cluster?.count}
                  </span>
                </div>
              ) : (
                <div className={`w-10 h-10 rounded-full shadow-warm-lg flex items-center justify-center ${
                  selectedBusiness?.id === cluster?.business?.id
                    ? 'bg-accent ring-4 ring-accent/30' :'bg-primary'
                }`}>
                  <Icon name="Store" size={20} color="var(--color-primary-foreground)" />
                </div>
              )}
            </button>
          );
        })}

        {userLocation && (
          <div
            className="absolute pointer-events-none"
            style={{
              left: `${((userLocation?.lng - mapCenter?.lng) * 10000) + 50}%`,
              top: `${((mapCenter?.lat - userLocation?.lat) * 10000) + 50}%`,
              transform: 'translate(-50%, -50%)'
            }}
          >
            <div className="relative">
              <div className="w-4 h-4 bg-accent rounded-full border-3 border-card shadow-warm-lg" />
              <div className="absolute inset-0 w-4 h-4 bg-accent rounded-full animate-ping opacity-75" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MapView;